// src/components/SsoRedirect.tsx
//
// ★★ ADR 0015 — NOT SIGNED IN, SO GO TO SINGLE SIGN-ON. No login page in between.
//
// Asks the gateway where its identity provider is (`GET /api/identities` mints a one-time authorization URL) and sends the whole
// browser there, remembering the page that was asked for so the person comes back to it. A deployment with NO identity provider
// (local development with the demonstration roster) has no URL to go to, and falls back to the `/login` page exactly as before.
//
// ⚠ Once per mount, from a ref, because React StrictMode runs an effect twice and each call mints a separate challenge: the second
// would replace the first in the gateway's memory and the person would arrive back with a `state` it no longer knows.
import { useEffect, useRef, useState } from 'react'
import { Navigate, useLocation } from 'react-router'
import { SgdsButton, SgdsSpinner } from '@govtechsg/sgds-web-component/react'
import { api, isNetworkError } from '../lib/api'
import { rememberReturnTo } from '../lib/sso-return'

export function SsoRedirect({ returnTo }: { readonly returnTo?: string }) {
  const location = useLocation()
  const started = useRef(false)
  const [noProvider, setNoProvider] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (started.current) return
    started.current = true
    api
      .identities()
      .then((result) => {
        if (result.authorizationUrl === undefined) {
          setNoProvider(true)
          return
        }
        rememberReturnTo(returnTo)
        window.location.href = result.authorizationUrl
      })
      .catch((error: unknown) => {
        setProblem(
          isNetworkError(error)
            ? 'PRS could not be reached. Check your network connection and try again. If it keeps failing, contact PRS support.'
            : error instanceof Error
              ? error.message
              : 'Could not start single sign-on.',
        )
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt])

  if (noProvider) return <Navigate to="/login" replace state={{ from: location }} />

  if (problem !== null) {
    return (
      <div className="min-h-screen grid place-items-center px-6">
        <div className="pt-card max-w-md text-center" role="alert">
          <p className="font-semibold text-[var(--sgds-danger-color-emphasis)]">Could not start sign-in</p>
          <p className="mt-2 text-[var(--sgds-body-color-subtle)]">{problem}</p>
          <div className="mt-4">
            <SgdsButton
              variant="outline"
              type="button"
              onClick={() => {
                started.current = false
                setProblem(null)
                setAttempt((n) => n + 1)
              }}
            >
              Try again
            </SgdsButton>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen grid place-items-center">
      <SgdsSpinner size="lg" label="Taking you to single sign-on…" orientation="vertical" />
    </div>
  )
}
